import { useEffect, useState } from "react";
import { useClerk } from "@clerk/react";
import { useNavigate } from "react-router-dom";
import { Download, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { StatusPill } from "@/components/privacy/RightsRequests";

const fmtSize = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);

const ExportRow = ({ x, onDownload }) => (
  <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3 text-sm" data-testid={`export-${x.id}`}>
    <StatusPill status={x.status} testid={`export-status-${x.id}`} />
    <span className="text-xs text-slate-500">Requested {fmtDate(x.requested_at)}{x.status === "ready" && ` · ${fmtSize(x.size)} · available until ${fmtDate(x.expires_at)}`}</span>
    {x.status === "preparing" && <Loader2 size={14} className="animate-spin text-slate-400" />}
    {x.status === "ready" && <Button size="sm" variant="outline" className="ml-auto rounded-full" data-testid={`export-download-${x.id}`} onClick={() => onDownload(x)}><Download size={14} className="mr-1.5" />Download</Button>}
  </li>
);

export const DownloadData = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/privacy/exports").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const preparing = rows.some((r) => r.status === "preparing");
  useEffect(() => { if (!preparing) return undefined; const t = setInterval(load, 2500); return () => clearInterval(t); }, [preparing]);
  const request = async () => { try { await api.post("/privacy/exports"); toast.success("We're preparing your data. It will appear here when it's ready."); load(); } catch (e) { toast.error(errMsg(e)); } };
  const download = async (x) => {
    try {
      const { data } = await api.get(`/privacy/exports/${x.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `middle-data-${x.ready_at.slice(0, 10)}.json` });
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.status === 410 ? "This download has expired. Request a new copy." : "Download failed"); load(); }
  };
  return (
    <section className="surface space-y-3 p-5 sm:p-6" data-testid="download-data">
      <h2 className="text-xl">Download my data</h2>
      <p className="text-xs text-slate-500">We gather a copy of what Middle holds about you (profile, posts, Shorts, messages, activity and privacy choices) into one file. It's prepared in the background and kept here for 7 days.</p>
      <Button data-testid="export-request-btn" className="rounded-full" disabled={preparing} onClick={request}>{preparing ? "Preparing…" : "Prepare a copy of my data"}</Button>
      {rows.length > 0 && <ul className="divide-y divide-border" data-testid="export-list">{rows.map((x) => <ExportRow key={x.id} x={x} onDownload={download} />)}</ul>}
    </section>
  );
};

export const DeleteAccount = () => {
  const { signOut } = useClerk();
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try { await api.post("/privacy/account/delete", { confirm: text }); toast.success("Your account has been deleted"); nav("/", { replace: true }); await signOut({ redirectUrl: "/" }); } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <section className="surface space-y-3 border-rose-200 p-5 sm:p-6" data-testid="delete-account">
      <h2 className="text-xl">Delete my account</h2>
      <p className="text-xs text-slate-500">This permanently deletes your account straight away: your profile, posts, Shorts, messages, follows and activity. It can't be undone. Download your data first if you want a copy.</p>
      <AlertDialog onOpenChange={(o) => !o && setText("")}>
        <AlertDialogTrigger asChild><Button variant="outline" className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="delete-account-btn"><Trash2 size={15} className="mr-1.5" />Delete my account</Button></AlertDialogTrigger>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader><AlertDialogTitle className="font-serif text-2xl font-medium">Delete your account permanently?</AlertDialogTitle>
            <AlertDialogDescription>Everything is deleted immediately and you'll be signed out. Type <b>DELETE</b> to confirm.</AlertDialogDescription></AlertDialogHeader>
          <Input data-testid="delete-account-confirm-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="DELETE" autoComplete="off" />
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" disabled={busy} data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
            <Button className="rounded-full bg-rose-600 hover:bg-rose-700" disabled={text.trim().toUpperCase() !== "DELETE" || busy} onClick={del} data-testid="delete-account-confirm-btn">{busy ? "Deleting…" : "Delete permanently"}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
